<script setup lang="ts">
import { computed, createTextVNode, Text, useSlots } from "vue";
import type { VNode } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";
import { DrawerUrlOptions } from "../../../store/drawers";
import { usePermission, type PropLike } from "../../../composables/permission";

const props = withDefaults(
  defineProps<{
    to?: null | RouteLocationRaw;
    toModal?: string | DrawerUrlOptions | object | false;
    toSlideover?: string | DrawerUrlOptions | object | false;

    text?: string | null;
    name?: string | null;
    tabindex?: number | null;

    disabled?: boolean;

    target?: string | null;
    href?: string | null;

    normal?: boolean;

    onClick?: () => void;
    inline?: boolean;
  }>(),
  { tabindex: 0 },
);

// ENG-21695 MLink's `to` allows null (falsy, handled in usePermission); PropLike omits null
const { hasLink, disabled, onClick } = usePermission(props as PropLike, false, useRouter());

const target = computed(() => {
  if (props.target) return props.target;
  if (!props.href) return undefined;

  const url = new URL(props.href || "", location.href);
  // ENG-21669: tel:/mailto:/sms: must not get target="_blank" — it flashes a blank
  // tab before the OS dialer/mail client takes over.
  if (["tel:", "mailto:", "sms:"].includes(url.protocol)) {
    return undefined;
  }
  if (url.host !== location.host) {
    return "_blank";
  }
  return undefined;
});

const slots = useSlots();

// ENG-21551: compiler leaves real whitespace in slot text, which then
// renders underlined. Trim only the outer edges, not internal spacing.
function trimSlotEdges(vnodes: VNode[]): VNode[] {
  if (!vnodes.length) return vnodes;
  const first = vnodes[0];
  if (first.type === Text && typeof first.children === "string") {
    first.children = first.children.replace(/^\s+/, "");
  }
  const last = vnodes[vnodes.length - 1];
  if (last.type === Text && typeof last.children === "string") {
    last.children = last.children.replace(/\s+$/, "");
  }
  return vnodes;
}

function LinkContent() {
  const content = slots.default
    ? slots.default()
    : [createTextVNode(props.name || props.text || "")];
  return trimSlotEdges(content);
}
</script>
<template>
  <component
    v-bind="$attrs"
    :is="
      disabled || !(hasLink || href || onClick)
        ? 'span'
        : to
        ? 'router-link'
        : $props.onClick
        ? 'button'
        : 'a'
    "
    :class="['m-link medicus-outline', { disabled, normal, inline }]"
    :to="to"
    :href="href"
    :target="target"
    :type="!disabled && $props.onClick ? 'button' : undefined"
    :tabindex="tabindex"
    :aria-label="name || text"
    @click="onClick"
    @keydown.enter="onClick"
  >
    <LinkContent />
  </component>
</template>

<style lang="scss" scoped>
.m-link {
  font-style: normal;
  // font-weight: bold;
  font-size: var(--font-size-md);
  line-height: 150%;
  /* identical to box height, or 143% */

  text-decoration-line: underline;
  text-underline-offset: 3px;

  background-color: transparent;
  border: none;

  cursor: pointer;
  color: var(--theme-blue);

  padding: 0;

  &:not(.inline) {
    display: flex;
  }

  &:focus-visible {
    outline: none;
  }

  &:hover {
    color: var(--theme-accent-blue);

    text-decoration-thickness: 3px;
  }

  &.normal {
    text-decoration: none;
    text-underline-offset: 1px;

    color: var(--text-color);

    &:hover {
      color: var(--text-color);

      text-decoration-line: underline;
      text-decoration-thickness: 1px;
    }
  }
}

.disabled {
  color: #90a0b7;
}
</style>
